interface FIREInput {
  currentAge: number
  targetRetirementAge: number
  annualExpenses: number
  currentInvestments: number
  annualSavings: number
  expectedReturn: number // annual %
  safeWithdrawalRate: number // % (default 4)
}

export function calculateFIRE(input: FIREInput) {
  const fiNumber = input.annualExpenses / (input.safeWithdrawalRate / 100)
  const yearsToTarget = input.targetRetirementAge - input.currentAge
  const monthlyReturn = input.expectedReturn / 100 / 12
  const monthlySavings = input.annualSavings / 12

  // Year-by-year projection to find FI crossover
  const projection = []
  let balance = input.currentInvestments
  let fiYear = null
  let fiAge = null
  const maxYears = Math.max(yearsToTarget + 10, 50) // project beyond target

  for (let year = 1; year <= maxYears; year++) {
    for (let month = 0; month < 12; month++) {
      const growth = balance * monthlyReturn
      balance += growth + monthlySavings
    }

    const age = input.currentAge + year
    const reachedFI = balance >= fiNumber

    if (reachedFI && fiYear === null) {
      fiYear = year
      fiAge = age
    }

    projection.push({
      year,
      age,
      balance: Math.round(balance),
      fiNumber: Math.round(fiNumber),
      annualSavings: Math.round(input.annualSavings),
      reachedFI,
    })

    // Stop projecting well past FI or at age 100
    if (age >= 100) break
  }

  const yearsToFI = fiYear || null
  const canRetireByTarget = fiAge !== null && fiAge <= input.targetRetirementAge

  // Coast FIRE: what balance today would grow to FI number by target age with $0 savings
  // FV = PV * (1 + r)^n => PV = FV / (1 + r)^n
  const annualReturn = input.expectedReturn / 100
  const coastFINumber = fiNumber / Math.pow(1 + annualReturn, yearsToTarget)
  const isCoastFI = input.currentInvestments >= coastFINumber

  // Sensitivity analysis: different SWR
  const sensitivityTable = [3, 3.5, 4, 4.5, 5].map(swr => {
    const finum = input.annualExpenses / (swr / 100)
    let bal = input.currentInvestments
    let years = null
    for (let y = 1; y <= 60; y++) {
      for (let m = 0; m < 12; m++) {
        bal += bal * monthlyReturn + monthlySavings
      }
      if (bal >= finum && years === null) {
        years = y
        break
      }
    }
    return {
      swr,
      fiNumber: Math.round(finum),
      yearsToFI: years,
      fiAge: years ? input.currentAge + years : null,
    }
  })

  // Savings rate
  const totalIncome = input.annualExpenses + input.annualSavings
  const savingsRate = totalIncome > 0 ? (input.annualSavings / totalIncome) * 100 : 0

  return {
    fiNumber: Math.round(fiNumber),
    yearsToFI,
    fiAge,
    canRetireByTarget,
    projection: projection.slice(0, Math.max(yearsToTarget + 10, (fiYear || 0) + 5, 20)),
    coastFINumber: Math.round(coastFINumber),
    isCoastFI,
    sensitivityTable,
    savingsRate: Math.round(savingsRate * 10) / 10,
    currentProgress: fiNumber > 0 ? Math.round((input.currentInvestments / fiNumber) * 1000) / 10 : 100,
    currentInvestments: input.currentInvestments,
    annualExpenses: input.annualExpenses,
    safeWithdrawalRate: input.safeWithdrawalRate,
  }
}
